import styled from "@emotion/styled";
import { useLiveQuery } from "dexie-react-hooks";
import { DateTime } from "luxon";
import { observer } from "mobx-react-lite";
import { useCallback, useMemo, useState } from "react";

import { MDSButton, MDSButtonSize, MDSButtonVariant } from "@/mds";
import { localBlobStoreModule } from "@/modules/local-blob-store";
import { LocalBlobIndexEntry } from "@/modules/local-blob-store/index/local-blob-index-entry";
import { BlobEntry } from "@/modules/local-blob-store/types";
import { useAppStore } from "@/store";

/**
 * Debug overlay panel for the Local Blob Store.
 *
 * A read-mostly inspection dashboard — the LBS is headless, so the only way to
 * see what's cached on a real device (esp. iOS, where DevTools has no OPFS
 * viewer) is here. Shows blob/orphan counts, object-URL counters, storage tier,
 * and budget; lists every cached blob in a unified view that joins the index
 * against OPFS (so torn and orphan state is visible), filterable by note id and
 * upload status. The only writes are the two budget controls. Ships to prod with
 * no discoverable entry point.
 */

type StatusFilter = "all" | "pending" | "uploaded";

/**
 * One cached blob as seen by the panel — the index entry joined with its OPFS
 * bytes. `presence` records which side(s) the id appears on: `both` is the
 * healthy case, `index-only` is a torn entry (row, no bytes), `opfs-only` is an
 * orphan (bytes, no row) that reconciliation would GC.
 */
interface UnifiedRow {
  attachmentId: string;
  /** The attachment's sync-model id, resolved via the attachments table. */
  modelId: string | undefined;
  mimeType: string | undefined;
  sizeBytes: number;
  uploadState: LocalBlobIndexEntry["uploadState"] | undefined;
  lastAccessAt: number | undefined;
  presence: "both" | "index-only" | "opfs-only";
}

interface StorageReadout {
  usage: number | undefined;
  quota: number | undefined;
  persisted: boolean;
}

function formatBytes(value: number | undefined): string {
  if (value === undefined) {
    return "—";
  }
  if (value < 1024) {
    return `${value} B`;
  }
  const units = ["KB", "MB", "GB", "TB"];
  let size = value / 1024;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(1)} ${units[unitIndex]}`;
}

/** Epoch millis → `MM/DD/YY - HH:mm:ss` in local time. */
function formatTimestamp(millis: number | undefined): string {
  if (millis === undefined) {
    return "—";
  }
  return DateTime.fromMillis(millis).toFormat("MM/dd/yy - HH:mm:ss");
}

export const LocalBlobStoreDebugPanel = observer(function LocalBlobStoreDebugPanel(): JSX.Element {
  const { store } = useAppStore();
  const lbs = store.localBlobStore;

  const [storage, setStorage] = useState<StorageReadout | null>(null);
  const [budget, setBudget] = useState<{ used: number; limit: number } | null>(null);
  const [budgetInput, setBudgetInput] = useState<string>("");
  const [status, setStatus] = useState<string>("Idle.");

  const handleRefreshStorage = useCallback(async () => {
    const estimate = (await navigator.storage?.estimate?.()) ?? {};
    // `persist()` is idempotent: it requests durable storage and returns whether
    // persistence is (now) granted — exactly the tier readout we want.
    const persisted = (await navigator.storage?.persist?.()) ?? false;
    setStorage({ usage: estimate.usage, quota: estimate.quota, persisted });
  }, []);

  const refreshBudget = useCallback(async () => {
    const [used, limit] = await Promise.all([
      store.memDb.localBlobIndex.getTotalSizeBytes(),
      lbs.currentBudget(),
    ]);
    setBudget({ used, limit });
  }, [lbs, store]);

  const handleForceEviction = useCallback(async () => {
    await lbs.enforceBudget();
    await refreshBudget();
    setStatus("enforceBudget done — uploaded LRU-evicted to budget.");
  }, [lbs, refreshBudget]);

  const handleSetBudget = useCallback(async () => {
    const trimmed = budgetInput.trim();
    if (trimmed === "") {
      lbs.setBudgetOverride(null);
      setStatus("Budget override cleared — back to auto (min 500MB / 50% quota).");
    } else {
      const bytes = Math.round(Number(trimmed) * 1024 * 1024);
      lbs.setBudgetOverride(bytes);
      setStatus(`Budget override set to ${formatBytes(bytes)}.`);
    }
    await refreshBudget();
  }, [budgetInput, lbs, refreshBudget]);

  return (
    <StyledContent>
      <StyledSectionTitle>Local Blob Store</StyledSectionTitle>
      <StyledStatus>{status}</StyledStatus>

      <StyledSectionTitle>Storage tier</StyledSectionTitle>
      <StyledButtonRow>
        <MDSButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.Default}
          onClick={() => void handleRefreshStorage()}
        >
          estimate() + persist()
        </MDSButton>
      </StyledButtonRow>
      {storage !== null && (
        <StyledStorageReadout>
          <StyledStorageRow>
            <span>usage</span>
            <span>{formatBytes(storage.usage)}</span>
          </StyledStorageRow>
          <StyledStorageRow>
            <span>quota</span>
            <span>{formatBytes(storage.quota)}</span>
          </StyledStorageRow>
          <StyledStorageRow>
            <span>persisted</span>
            <span>{String(storage.persisted)}</span>
          </StyledStorageRow>
        </StyledStorageReadout>
      )}

      <StyledSectionTitle>Budget &amp; eviction</StyledSectionTitle>
      <StyledButtonRow>
        <MDSButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.Default}
          onClick={() => void refreshBudget()}
        >
          refresh used / budget
        </MDSButton>
        <MDSButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.Default}
          onClick={() => void handleForceEviction()}
        >
          force eviction
        </MDSButton>
      </StyledButtonRow>
      {budget !== null && (
        <StyledStorageReadout>
          <StyledStorageRow>
            <span>used</span>
            <span>{formatBytes(budget.used)}</span>
          </StyledStorageRow>
          <StyledStorageRow>
            <span>budget</span>
            <span>{formatBytes(budget.limit)}</span>
          </StyledStorageRow>
        </StyledStorageReadout>
      )}

      <StyledLabel>budget override (MB — empty = auto)</StyledLabel>
      <StyledInput
        value={budgetInput}
        onChange={event => setBudgetInput(event.target.value)}
        placeholder="e.g. 1 or 0.5"
        inputMode="decimal"
        spellCheck={false}
      />
      <StyledButtonRow>
        <MDSButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.Default}
          onClick={() => void handleSetBudget()}
        >
          apply budget override
        </MDSButton>
      </StyledButtonRow>

      <UnifiedBlobTable />
    </StyledContent>
  );
});

/**
 * Live, unified view of every cached blob — the index joined with OPFS bytes.
 * Backed by Dexie `liveQuery`, so rows appear / change as the API writes them.
 * Filterable by note id (resolved through the attachments table, since the index
 * itself carries no note id) and by upload status.
 */
function UnifiedBlobTable(): JSX.Element {
  const { store } = useAppStore();
  const [noteIdFilter, setNoteIdFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const trimmedNoteId = noteIdFilter.trim();

  const result = useLiveQuery<{
    rows: UnifiedRow[];
    noteAttachmentIds: Set<string> | null;
  }>(async () => {
    if (!store.db.isInitialized()) {
      return { rows: [], noteAttachmentIds: null };
    }

    // Dexie reads first, with no non-Dexie await interleaved: liveQuery builds
    // its change subscription from these, and a non-Dexie await mixed in (the
    // OPFS listBlobs below) would break the zone and drop the subscription.
    const [entries, attachments] = await Promise.all([
      store.memDb.localBlobIndex.dexieTable.toArray(),
      store.spaceAccountNoteAttachments.localTable.toArray(),
    ]);

    // OPFS read after the subscription is established. The index toArray above
    // drives re-runs; this isn't reactive to OPFS-only changes on its own.
    const files = await localBlobStoreModule.listBlobs();

    // The index keys on `associated_note_attachment_id`; map it to the
    // attachment's sync-model id so the table can show `model_id`.
    const modelIdByAttachmentId = new Map<string, string>();
    for (const attachment of attachments) {
      const attachmentId = attachment.model_data.associated_note_attachment_id;
      if (attachmentId) {
        modelIdByAttachmentId.set(attachmentId, attachment.model_id);
      }
    }

    const rows = joinIndexAndOpfs(entries, files, modelIdByAttachmentId);

    // The index has no note id; the note ↔ attachment link lives in the
    // attachments table, keyed on `associated_note_id`.
    let noteAttachmentIds: Set<string> | null = null;
    if (trimmedNoteId !== "") {
      noteAttachmentIds = new Set(
        attachments
          .filter(attachment => attachment.associated_note_id === trimmedNoteId)
          .map(attachment => attachment.model_data.associated_note_attachment_id)
          .filter((id): id is string => Boolean(id))
      );
    }

    return { rows, noteAttachmentIds };
  }, [store, trimmedNoteId]);

  const allRows = result?.rows;
  const noteAttachmentIds = result?.noteAttachmentIds ?? null;

  const filteredRows = useMemo(() => {
    if (allRows === undefined) {
      return undefined;
    }
    return allRows.filter(row => {
      if (statusFilter !== "all" && row.uploadState !== statusFilter) {
        return false;
      }
      if (noteAttachmentIds !== null && !noteAttachmentIds.has(row.attachmentId)) {
        return false;
      }
      return true;
    });
  }, [allRows, statusFilter, noteAttachmentIds]);

  const totalCount = allRows?.length ?? 0;
  const orphanCount = allRows?.filter(row => row.presence === "opfs-only").length ?? 0;

  return (
    <>
      <StyledSectionTitle>Blobs &amp; index (live)</StyledSectionTitle>

      <StyledStorageReadout>
        <StyledStorageRow>
          <span>blob count</span>
          <span>{totalCount}</span>
        </StyledStorageRow>
        <StyledStorageRow>
          <span>orphaned blobs (OPFS, no index)</span>
          <span>{orphanCount}</span>
        </StyledStorageRow>
        <StyledStorageRow>
          <span>showing</span>
          <span>
            {filteredRows?.length ?? 0} of {totalCount}
          </span>
        </StyledStorageRow>
      </StyledStorageReadout>

      <StyledLabel>filter by note id</StyledLabel>
      <StyledInput
        value={noteIdFilter}
        onChange={event => setNoteIdFilter(event.target.value)}
        placeholder="paste a note id"
        spellCheck={false}
      />
      <StyledButtonRow>
        <MDSButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.Default}
          onClick={() => setNoteIdFilter(store.routing.noteIdParam ?? "")}
        >
          use current note
        </MDSButton>
        <MDSButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.Default}
          onClick={() => setNoteIdFilter("")}
        >
          clear
        </MDSButton>
      </StyledButtonRow>

      <StyledLabel>filter by status</StyledLabel>
      <StyledButtonRow>
        {(["all", "pending", "uploaded"] as const).map(value => (
          <MDSButton
            key={value}
            size={MDSButtonSize.Small}
            variant={statusFilter === value ? MDSButtonVariant.Emphasis : MDSButtonVariant.Default}
            onClick={() => setStatusFilter(value)}
          >
            {value}
          </MDSButton>
        ))}
      </StyledButtonRow>

      {filteredRows === undefined ? (
        <StyledStatus>Loading…</StyledStatus>
      ) : filteredRows.length === 0 ? (
        <StyledStatus>No matching blobs.</StyledStatus>
      ) : (
        <StyledIndexTable>
          <StyledIndexHeaderRow>
            <span>model id</span>
            <span>mimeType</span>
            <span>size</span>
            <span>state</span>
            <span>presence</span>
            <span>lastAccessAt</span>
          </StyledIndexHeaderRow>
          {filteredRows.map(row => (
            <StyledIndexRow key={row.attachmentId}>
              <StyledBlobId title={row.modelId}>{row.modelId ?? "—"}</StyledBlobId>
              <StyledBlobId title={row.mimeType}>{row.mimeType ?? "—"}</StyledBlobId>
              <span>{formatBytes(row.sizeBytes)}</span>
              <span>{row.uploadState ?? "—"}</span>
              <span>{row.presence}</span>
              <span>{formatTimestamp(row.lastAccessAt)}</span>
            </StyledIndexRow>
          ))}
        </StyledIndexTable>
      )}
    </>
  );
}

/**
 * Join the index rows against the OPFS blob listing by id. Most ids appear on
 * both sides; the divergent ones (torn index rows / orphan OPFS files) surface
 * via `presence`. Index `sizeBytes` wins when present, else the OPFS byte size.
 */
function joinIndexAndOpfs(
  entries: LocalBlobIndexEntry[],
  files: BlobEntry[],
  modelIdByAttachmentId: Map<string, string>
): UnifiedRow[] {
  const fileById = new Map(files.map(file => [file.id, file]));
  const entryById = new Map(entries.map(entry => [entry.attachmentId, entry]));
  const ids = new Set<string>([...entryById.keys(), ...fileById.keys()]);

  const rows: UnifiedRow[] = [];
  for (const id of ids) {
    const entry = entryById.get(id);
    const file = fileById.get(id);
    const presence =
      entry !== undefined && file !== undefined
        ? "both"
        : entry !== undefined
          ? "index-only"
          : "opfs-only";
    rows.push({
      attachmentId: id,
      modelId: modelIdByAttachmentId.get(id),
      mimeType: entry?.mimeType,
      sizeBytes: entry?.sizeBytes ?? file?.size ?? 0,
      uploadState: entry?.uploadState,
      lastAccessAt: entry?.lastAccessAt,
      presence,
    });
  }
  return rows;
}

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledSectionTitle = styled.h4`
  margin: 8px 0 0 0;
  font-size: 14px;
  font-weight: 600;
`;

const StyledLabel = styled.label`
  font-size: 11px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const StyledInput = styled.input`
  background-color: ${({ theme }) => theme.colors.surface.modal};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: monospace;
  font-size: 12px;
  padding: 8px;
`;

const StyledButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const StyledStatus = styled.div`
  font-family: monospace;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  word-break: break-word;
`;

const StyledStorageReadout = styled.div`
  display: flex;
  flex-direction: column;
  font-family: monospace;
  font-size: 12px;
`;

const StyledStorageRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};

  &:last-child {
    border-bottom: none;
  }
`;

const StyledBlobId = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledIndexTable = styled.div`
  display: flex;
  flex-direction: column;
  font-family: monospace;
  font-size: 11px;
`;

const StyledIndexRow = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1.2fr 0.7fr 0.8fr 0.8fr 1.8fr;
  gap: 8px;
  padding: 4px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};

  &:last-child {
    border-bottom: none;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const StyledIndexHeaderRow = styled(StyledIndexRow)`
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
`;
